import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Trans } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api } from '../api/client';
import {
  isAccessDeniedError,
  PRACTICE_TYPE_ICONS,
  type PostSessionBody,
  type PostSessionResponse,
  type Practice as PracticeModel,
  type PracticeAudio,
  type SessionMode,
  type TodayPlan,
} from '../api/types';
import {
  formatDurationLong,
  formatMinutesShort,
  formatNumber,
  formatPrice,
  languageName,
  languageNameIn,
  localizedPlanText,
  practiceTypeLabel,
  traditionLabel,
  useJourneyLocale,
} from '../i18n/journey';
import { useUser } from '../state/UserContext';

// Practice player (review finding 8 + the review's "Practice"/"After practice" UI direction).
//
// One explicit state machine drives everything the person sees:
//   loading -> ready -> playing <-> paused -> ended -> saving -> saved
//   (plus `error` if the practice itself couldn't load, and `locked` for a non-available level)
// Narration has its own small state (idle/loading/ready/unavailable/error) because it is a
// separate failure domain: it can arrive after Start (and then joins in), fail, or expire, and in
// every case the silent timer and transcript keep working.
//
// Time is accounted monotonically: elapsed = accumulated running segments measured with
// performance.now(), never "decrement a counter once per interval" -- so a throttled background
// tab can't drift the timer, and the duration sent to the server is what actually elapsed.
// Nothing is saved before Start except an explicitly self-reported completion, which is labeled
// as such and carries no fabricated duration.

const ACCESS_DENIED_KEYS: Record<string, string> = {
  premium_required: 'practice.access.premium_required',
  prerequisite_required: 'practice.access.prerequisite_required',
  unpublished: 'practice.access.unpublished',
};

function accessDeniedMessage(t: TFunction, lang: string, access: string): string | null {
  const key = ACCESS_DENIED_KEYS[access];
  return key ? t(key, { price: formatPrice(lang) }) : null;
}

type PlayerState = 'loading' | 'error' | 'locked' | 'ready' | 'playing' | 'paused' | 'ended' | 'saving' | 'saved';
type NarrationState = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

interface SavedSummary {
  result: PostSessionResponse;
  mode: SessionMode;
  durationSeconds: number | null;
}

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Recorded on the session occurrence for context only -- the server still decides the streak
 * day from its own clock and the stored timezone. Only a plausible IANA name is sent, since the
 * server validates it strictly. */
function clientContext(): Pick<PostSessionBody, 'timezone' | 'localDate'> {
  const now = new Date();
  const localDate = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }
  const plausible = !!timezone && /^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+)+)$/.test(timezone);
  return plausible ? { localDate, timezone } : { localDate };
}

function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return `web-${c.randomUUID()}`;
  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function narrationFallbackNote(audio: PracticeAudio, t: TFunction, lang: string): string | null {
  if (!audio.fallback) return null;
  const values = {
    requested: languageNameIn(lang, audio.requestedLocale),
    actual: languageNameIn(lang, audio.audioLocale),
  };
  if (audio.fallbackReason === 'voice_unavailable') {
    return t('practice.narration.fallbackVoice', values);
  }
  return t('practice.narration.fallbackTranslation', values);
}

export function Practice() {
  const { id } = useParams<{ id: string }>();
  const { t } = useJourneyLocale();
  if (!id) {
    return (
      <div className="empty-state">
        <p>{t('practice.notFound')}</p>
        <Link to="/home" className="link-back">&larr; {t('practice.backToToday')}</Link>
      </div>
    );
  }
  // Keyed by practice id: moving to another practice remounts the player, so the timer, audio
  // element and URL, save state and idempotency key can never leak from one practice to the next.
  return <PracticePlayer key={id} practiceId={id} />;
}

function PracticePlayer({ practiceId }: { practiceId: string }) {
  const { applySessionResult } = useUser();
  const { t, lang } = useJourneyLocale();
  // Messages set from async callbacks read the current translator through a ref, so those
  // callbacks keep their original dependencies (a language change must not refetch or restart).
  const tRef = useRef(t);
  tRef.current = t;
  const langRef = useRef(lang);
  langRef.current = lang;

  const [practice, setPractice] = useState<PracticeModel | null>(null);
  const [state, setState] = useState<PlayerState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  const [audio, setAudio] = useState<PracticeAudio | null>(null);
  const [narration, setNarrationValue] = useState<NarrationState>('idle');
  const [narrationMessage, setNarrationMessage] = useState<string | null>(null);
  const [narrationFinished, setNarrationFinished] = useState(false);

  const [showTranscript, setShowTranscript] = useState(false);
  const [confirmSelfReport, setConfirmSelfReport] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedSummary | null>(null);
  const [nextStep, setNextStep] = useState<TodayPlan | null>(null);
  const [, setTick] = useState(0);

  const mountedRef = useRef(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stateRef = useRef<PlayerState>('loading');
  const narrationRef = useRef<NarrationState>('idle');
  const accumulatedMsRef = useRef(0);
  const segmentStartRef = useRef<number | null>(null);
  const startedAtRef = useRef<string | null>(null);
  const narrationPlayedRef = useRef(false);
  // The last mounted <audio>: React has already cleared audioRef when unmount cleanup runs.
  const lastAudioElRef = useRef<HTMLAudioElement | null>(null);
  // performance.now() when the current signed URL arrived. Expiry is judged against the server's
  // expiresAt only once a URL is over a minute old, so a skewed device clock can't make every
  // fresh URL look expired and refetch in a loop.
  const audioReceivedAtRef = useRef(0);
  // Set when the browser blocked a play() begun outside a tap (Safari/iOS); the next Retry tap
  // plays the already-loaded URL directly, inside the gesture, instead of refetching.
  const playBlockedRef = useRef(false);
  const resumePositionRef = useRef(0);
  // The exact body of the first save attempt. Retries resend it unchanged (same idempotencyKey),
  // so a retry after a lost response can never double-count the session.
  const pendingBodyRef = useRef<PostSessionBody | null>(null);
  const savingRef = useRef(false);

  const go = useCallback((next: PlayerState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const setNarration = useCallback((next: NarrationState) => {
    narrationRef.current = next;
    setNarrationValue(next);
  }, []);

  const plannedMs = Math.max(0, (practice?.durationSeconds ?? 0) * 1000);

  const elapsedMs = useCallback(
    () => accumulatedMsRef.current + (segmentStartRef.current !== null ? performance.now() - segmentStartRef.current : 0),
    [],
  );

  // Mount/unmount: stop and release the audio element when leaving the practice.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const el = lastAudioElRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
    };
  }, []);

  // Load the practice (content and access state).
  useEffect(() => {
    let cancelled = false;
    go('loading');
    setLoadError(null);
    api
      .getPractices({})
      .then((res) => {
        if (cancelled) return;
        const found = res.practices.find((p) => p.practiceId === practiceId) ?? null;
        setPractice(found);
        if (!found) {
          setLoadError(tRef.current('practice.notFound'));
          go('error');
          return;
        }
        go(found.access === 'available' ? 'ready' : 'locked');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : tRef.current('practice.loadFailed'));
        go('error');
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, loadAttempt, go]);

  const loadNarration = useCallback(async () => {
    setNarration('loading');
    setNarrationMessage(null);
    try {
      const res = await api.getPracticeAudio(practiceId);
      if (!mountedRef.current) return;
      audioReceivedAtRef.current = performance.now();
      setAudio(res);
      setNarration('ready');
    } catch (e: unknown) {
      if (!mountedRef.current) return;
      const status = isAccessDeniedError(e) ? e.status : 0;
      if (status === 404) {
        setNarration('unavailable');
        setNarrationMessage(tRef.current('practice.narration.noNarration'));
      } else {
        setNarration('error');
        setNarrationMessage(tRef.current('practice.narration.loadError'));
      }
      setShowTranscript(true);
    }
  }, [practiceId, setNarration]);

  // Narration is fetched as soon as the practice is known to be playable, so it is usually ready
  // before Start -- but Start never waits for it.
  useEffect(() => {
    if (practice?.access === 'available' && narrationRef.current === 'idle') void loadNarration();
  }, [practice, loadNarration]);

  const retryNarration = useCallback(() => {
    const el = audioRef.current;
    if (playBlockedRef.current && el && el.getAttribute('src')) {
      playBlockedRef.current = false;
      setNarration('ready');
      setNarrationMessage(null);
      if (stateRef.current === 'playing') void el.play().catch(() => undefined);
      return;
    }
    resumePositionRef.current = el && Number.isFinite(el.currentTime) ? el.currentTime : 0;
    void loadNarration();
  }, [loadNarration]);

  const pauseSession = useCallback(() => {
    if (stateRef.current !== 'playing') return;
    if (segmentStartRef.current !== null) {
      accumulatedMsRef.current += performance.now() - segmentStartRef.current;
      segmentStartRef.current = null;
    }
    go('paused');
    audioRef.current?.pause();
  }, [go]);

  const endSession = useCallback(() => {
    if (stateRef.current !== 'playing') return;
    if (segmentStartRef.current !== null) {
      accumulatedMsRef.current += performance.now() - segmentStartRef.current;
      segmentStartRef.current = null;
    }
    // The timer ends at the planned duration; a late tick must not add phantom seconds.
    if (plannedMs > 0) accumulatedMsRef.current = Math.min(accumulatedMsRef.current, plannedMs);
    go('ended');
    audioRef.current?.pause();
  }, [go, plannedMs]);

  const startSession = useCallback(() => {
    const current = stateRef.current;
    if ((current !== 'ready' && current !== 'paused') || pendingBodyRef.current) return;
    if (!startedAtRef.current) startedAtRef.current = new Date().toISOString();
    segmentStartRef.current = performance.now();
    setConfirmSelfReport(false);
    go('playing');
    // Start narration inside the tap itself: Safari/iOS reject a play() begun later from an
    // effect. The sync effect below still covers narration that arrives after Start.
    const el = audioRef.current;
    if (el && el.paused && !el.ended && narrationRef.current === 'ready') {
      void el.play().catch(() => undefined); // real failures are reported by the sync effect
    }
  }, [go]);

  const startOver = useCallback(() => {
    if (stateRef.current !== 'paused' || pendingBodyRef.current) return;
    accumulatedMsRef.current = 0;
    segmentStartRef.current = null;
    startedAtRef.current = null;
    narrationPlayedRef.current = false;
    setNarrationFinished(false);
    const el = audioRef.current;
    if (el) el.currentTime = 0;
    go('ready');
  }, [go]);

  // Keep narration in step with the session: play whenever the session is running and narration
  // is ready -- including narration that arrives AFTER Start, and after Resume. A URL close to
  // expiry is refreshed first (a fresh signed URL, resuming at the same position).
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audio || state !== 'playing' || narration !== 'ready' || narrationFinished) return;
    if (Date.parse(audio.expiresAt) - Date.now() < 30_000 && performance.now() - audioReceivedAtRef.current > 60_000) {
      retryNarration();
      return;
    }
    if (!el.paused) return;
    el.play().catch((err: unknown) => {
      if (!mountedRef.current) return;
      if (err instanceof DOMException && err.name === 'AbortError') return; // superseded by pause/src change
      if (err instanceof DOMException && err.name === 'NotAllowedError') playBlockedRef.current = true;
      setNarration('error');
      setNarrationMessage(tRef.current('practice.narration.startError'));
      setShowTranscript(true);
    });
  }, [state, audio, narration, narrationFinished, retryNarration, setNarration]);

  // Re-render while running (display only -- time itself is derived from timestamps), end at the
  // planned duration, and catch up immediately when a hidden tab becomes visible again.
  useEffect(() => {
    if (state !== 'playing') return undefined;
    const check = () => {
      setTick((t) => t + 1);
      if (plannedMs > 0 && elapsedMs() >= plannedMs) endSession();
    };
    const interval = window.setInterval(check, 250);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
    };
  }, [state, plannedMs, elapsedMs, endSession]);

  const timedMode = (): SessionMode => (narrationPlayedRef.current ? 'guided' : 'silent');

  const save = useCallback(
    async (mode: SessionMode) => {
      if (!practice || savingRef.current) return; // double-click safe
      const from = stateRef.current;
      if (!pendingBodyRef.current) {
        const allowed = mode === 'self_reported' ? from === 'ready' : from === 'ended' || from === 'paused';
        if (!allowed) return;
        const completedAt = new Date().toISOString();
        pendingBodyRef.current =
          mode === 'self_reported'
            ? {
                practiceId: practice.practiceId,
                idempotencyKey: newIdempotencyKey(),
                mode,
                completedAt,
                contentVersion: practice.contentVersion,
                ...clientContext(),
              }
            : {
                practiceId: practice.practiceId,
                idempotencyKey: newIdempotencyKey(),
                mode,
                durationSeconds: Math.round(elapsedMs() / 1000),
                startedAt: startedAtRef.current ?? completedAt,
                completedAt,
                contentVersion: practice.contentVersion,
                ...clientContext(),
              };
      }
      const body = pendingBodyRef.current;
      savingRef.current = true;
      setSaveError(null);
      go('saving');
      try {
        const result = await api.postSession(body);
        if (!mountedRef.current) return;
        applySessionResult(result);
        setSaved({ result, mode: body.mode ?? mode, durationSeconds: body.durationSeconds ?? null });
        go('saved');
        api
          .getToday()
          .then((plan) => {
            if (mountedRef.current) setNextStep(plan);
          })
          .catch(() => {
            // The next-step suggestion is optional; the Practices link below always works.
          });
      } catch (e: unknown) {
        if (!mountedRef.current) return;
        const denied = isAccessDeniedError(e) && e.access ? accessDeniedMessage(tRef.current, langRef.current, e.access) : null;
        if (denied) {
          setSaveError(denied);
        } else {
          setSaveError(
            tRef.current('practice.saveFailed', {
              message: e instanceof Error ? e.message : tRef.current('practice.saveFailedGeneric'),
            }),
          );
        }
        go(from);
      } finally {
        savingRef.current = false;
      }
    },
    [practice, elapsedMs, go, applySessionResult],
  );

  if (state === 'loading') {
    return (
      <div className="center-page" style={{ minHeight: '40vh' }}>
        <div className="spinner" />
        <p className="page-subtitle">{t('practice.preparing')}</p>
      </div>
    );
  }

  if (state === 'error' || !practice) {
    return (
      <div className="empty-state">
        <p role="alert">{loadError ?? t('practice.loadErrorFallback')}</p>
        <button type="button" className="btn btn-secondary" onClick={() => setLoadAttempt((n) => n + 1)}>
          {t('shared.tryAgain')}
        </button>
        <Link to="/home" className="link-back">&larr; {t('practice.backToToday')}</Link>
      </div>
    );
  }

  if (state === 'locked') {
    // Reached via a direct link, an old bookmark, or a stale client cache -- the server would
    // reject both the audio fetch and session completion for this practice (see
    // app.domain.resolve_practice_access), so this state is shown instead of a broken player.
    return (
      <div className="empty-state">
        <h1 className="page-title">{practice.title}</h1>
        <p>{accessDeniedMessage(t, lang, practice.access) ?? t('practice.notAvailable')}</p>
        {practice.access === 'premium_required' && (
          <p>
            <Link to="/settings?tab=billing" className="btn btn-secondary">
              {t('practices.locked.seePremium')}
            </Link>
          </p>
        )}
        <Link to="/practices" className="link-back">&larr; {t('practice.backToPractices')}</Link>
      </div>
    );
  }

  const elapsed = elapsedMs();
  const elapsedSeconds = Math.floor(elapsed / 1000);
  const displaySeconds = plannedMs > 0 ? Math.max(0, Math.ceil((plannedMs - elapsed) / 1000)) : elapsedSeconds;
  const progressPct = plannedMs > 0 ? Math.min(100, Math.round((elapsed / plannedMs) * 100)) : 0;
  const pending = pendingBodyRef.current;
  // The practice text (title, description, transcript) is in the narration's language exactly
  // when the server reports the translation itself is missing -- marked with `lang` so assistive
  // technology pronounces it correctly. Otherwise it is in the requested (UI) language.
  const contentLang = audio?.fallback && audio.fallbackReason === 'translation_missing' ? audio.audioLocale : undefined;

  const header = (
    <>
      <div className="back-row">
        <Link to="/home" className="link-back">&larr; {t('practice.back')}</Link>
      </div>
      <header className="pp-header">
        <p className="pp-meta">
          {traditionLabel(t, practice.tradition)} · {PRACTICE_TYPE_ICONS[practice.type]}{' '}
          {practiceTypeLabel(t, practice.type)} · {formatMinutesShort(lang, practice.durationSeconds, true)}
        </p>
        <h1 className="page-title" lang={contentLang}>{practice.title}</h1>
        {state !== 'saved' && <p className="pp-lead" lang={contentLang}>{practice.description}</p>}
      </header>
    </>
  );

  if (state === 'saved' && saved) {
    return (
      <div className="pp-player">
        {header}
        <AfterPractice practiceId={practice.practiceId} saved={saved} nextStep={nextStep} />
      </div>
    );
  }

  let primary: ReactNode;
  if (pending) {
    primary = (
      <button
        type="button"
        className="btn btn-primary pp-main"
        onClick={() => void save(pending.mode ?? 'silent')}
        disabled={state === 'saving'}
      >
        {state === 'saving' ? t('practice.saving') : t('practice.trySavingAgain')}
      </button>
    );
  } else if (state === 'ready') {
    primary = (
      <button type="button" className="btn btn-primary pp-main" onClick={startSession}>
        {t('practice.start')}
      </button>
    );
  } else if (state === 'playing') {
    primary = (
      <button type="button" className="btn btn-primary pp-main" onClick={pauseSession}>
        {t('practice.pause')}
      </button>
    );
  } else if (state === 'paused') {
    primary = (
      <button type="button" className="btn btn-primary pp-main" onClick={startSession}>
        {t('practice.resume')}
      </button>
    );
  } else {
    primary = (
      <button
        type="button"
        className="btn btn-primary pp-main"
        onClick={() => void save(timedMode())}
        disabled={state === 'saving'}
      >
        {state === 'saving' ? t('practice.saving') : t('practice.savePractice')}
      </button>
    );
  }

  const liveMessage =
    state === 'ended'
      ? t('practice.live.ended')
      : state === 'saving'
        ? t('practice.live.saving')
        : state === 'paused'
          ? t('practice.live.paused')
          : '';

  const clock = formatClock(displaySeconds);

  return (
    // Wide screens (CSS): reading on the left, the session card (narration, timer, controls) in a
    // sticky column on the right. Phones keep one column: read, then practice, then transcript.
    <div className="pp-player pp-split">
      {header}

      {practice.guidanceRequired && (
        <aside className="pp-guidance" aria-label={t('practice.guidance.ariaLabel')}>
          <p className="pp-guidance-title">{t('practice.guidance.title')}</p>
          <p>{t('practice.guidance.body')}</p>
        </aside>
      )}

      <details className="pp-sources">
        <summary>{t('practice.sources.summary')}</summary>
        <p>
          {practice.reviewerStatus === 'reviewed' && practice.reviewerName
            ? t('practice.sources.reviewedBy', { name: practice.reviewerName })
            : t('practice.sources.draft')}
        </p>
        {practice.sourceRefs.length > 0 && (
          <p>{t('practice.sources.list', { sources: practice.sourceRefs.join(', ') })}</p>
        )}
      </details>

      {practice.type === 'service' && state === 'ready' && <p className="pp-note">{t('practice.serviceNote')}</p>}

      <div className="pp-session">
        <NarrationStatus
          narration={narration}
          audio={audio}
          message={narrationMessage}
          finished={narrationFinished}
          onRetry={retryNarration}
        />

        {audio && (
          // One media model: no native controls, no loop. The single Start/Pause button below
          // drives both the timer and narration. A pause from outside the page (OS media keys,
          // headphones) pauses the whole session, so the two can never disagree.
          // eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript below is the caption
          <audio
            ref={(el) => {
              audioRef.current = el;
              if (el) lastAudioElRef.current = el;
            }}
            src={audio.url}
            preload="auto"
            onPlay={() => {
              // OS media keys / headphones pressed play: resume the whole session, or refuse.
              if (stateRef.current === 'playing') return;
              startSession();
              // startSession may refuse (e.g. ended/saving); re-read the ref it just updated.
              if ((stateRef.current as PlayerState) !== 'playing') audioRef.current?.pause();
            }}
            onPlaying={() => {
              narrationPlayedRef.current = true;
            }}
            onPause={() => {
              const el = audioRef.current;
              if (stateRef.current === 'playing' && narrationRef.current === 'ready' && el && !el.ended) pauseSession();
            }}
            onEnded={() => setNarrationFinished(true)}
            onError={() => {
              if (narrationRef.current !== 'ready') return;
              setNarration('error');
              setNarrationMessage(tRef.current('practice.narration.stopped'));
              setShowTranscript(true);
            }}
            onLoadedMetadata={() => {
              const el = audioRef.current;
              if (el && resumePositionRef.current > 0) {
                el.currentTime = resumePositionRef.current;
                resumePositionRef.current = 0;
              }
            }}
          />
        )}

        <div className="timer-wrap">
          <div
            className="timer-circle"
            style={{ '--progress': progressPct } as CSSProperties}
            role="timer"
            aria-label={
              plannedMs > 0 ? t('practice.timer.remaining', { time: clock }) : t('practice.timer.elapsed', { time: clock })
            }
          >
            <div className="timer-circle-inner">
              <div className="timer-time">{clock}</div>
              <div className="timer-label">{t(`practice.state.${state}`)}</div>
            </div>
          </div>

          <div className="pp-controls">{primary}</div>

          {!pending && state === 'paused' && (
            <div className="pp-secondary">
              {elapsedSeconds >= 1 && (
                <button type="button" className="pp-link-button" onClick={() => void save(timedMode())}>
                  {t('practice.finishNow', { duration: formatDurationLong(lang, elapsedSeconds) })}
                </button>
              )}
              <button type="button" className="pp-link-button" onClick={startOver}>
                {t('practice.startOver')}
              </button>
            </div>
          )}
        </div>

        <p className="pp-visually-hidden" aria-live="polite">
          {liveMessage}
        </p>
        {saveError && (
          <p className="error-text" role="alert">
            {saveError}
          </p>
        )}

        {state === 'ready' && !pending && (
          <div className="pp-self-report-row">
            {confirmSelfReport ? (
              <div className="pp-self-report" role="group" aria-label={t('practice.selfReport.ariaLabel')}>
                <p>
                  <Trans i18nKey="practice.selfReport.body" components={{ strong: <strong /> }} />
                </p>
                <div className="pp-secondary">
                  <button type="button" className="btn btn-secondary" onClick={() => void save('self_reported')}>
                    {t('practice.selfReport.save')}
                  </button>
                  <button type="button" className="pp-link-button" onClick={() => setConfirmSelfReport(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="pp-link-button" onClick={() => setConfirmSelfReport(true)}>
                {t('practice.selfReport.open')}
              </button>
            )}
          </div>
        )}
      </div>

      {practice.transcript ? (
        <div className="transcript-panel">
          <button
            type="button"
            className="transcript-toggle"
            onClick={() => setShowTranscript((v) => !v)}
            aria-expanded={showTranscript}
          >
            {showTranscript
              ? t('practice.transcript.hide')
              : narration === 'ready'
                ? t('practice.transcript.show')
                : t('practice.transcript.readAlong')}
          </button>
          {showTranscript && (
            <p className="transcript-text" lang={contentLang}>
              {practice.transcript}
            </p>
          )}
        </div>
      ) : (
        (narration === 'unavailable' || narration === 'error') && (
          <p className="pp-note">{t('practice.transcript.none')}</p>
        )
      )}
    </div>
  );
}

function NarrationStatus({
  narration,
  audio,
  message,
  finished,
  onRetry,
}: {
  narration: NarrationState;
  audio: PracticeAudio | null;
  message: string | null;
  finished: boolean;
  onRetry: () => void;
}) {
  const { t, lang } = useJourneyLocale();
  if (narration === 'idle' || narration === 'loading') {
    return (
      <div className="pp-narration" role="status">
        <span className="pp-badge pp-badge-quiet">{t('practice.narration.badge')}</span>
        <span className="pp-note">{t('practice.narration.preparing')}</span>
      </div>
    );
  }
  if (narration === 'ready' && audio) {
    const note = narrationFallbackNote(audio, t, lang);
    return (
      <div className="pp-narration" role="status">
        {/* Always the language actually narrated (audioLocale), never the UI language. */}
        <span className="pp-badge">
          {t('practice.narration.badge')} · <span lang={audio.audioLocale}>{languageName(audio.audioLocale)}</span>
        </span>
        {finished && <span className="pp-note">{t('practice.narration.finished')}</span>}
        {note && <p className="pp-note">{note}</p>}
      </div>
    );
  }
  return (
    <div className="pp-narration" role="status">
      <span className="pp-badge pp-badge-quiet">
        {narration === 'unavailable' ? t('practice.narration.silent') : t('practice.narration.unavailable')}
      </span>
      {message && <span className="pp-note">{message}</span>}
      {narration === 'error' && (
        <button type="button" className="pp-link-button" onClick={onRetry}>
          {t('practice.narration.retry')}
        </button>
      )}
    </div>
  );
}

function AfterPractice({
  practiceId,
  saved,
  nextStep,
}: {
  practiceId: string;
  saved: SavedSummary;
  nextStep: TodayPlan | null;
}) {
  const { t, lang } = useJourneyLocale();
  const { result, mode, durationSeconds } = saved;
  const duration = formatDurationLong(lang, durationSeconds ?? 0);
  const acknowledgement =
    mode === 'self_reported'
      ? t('practice.after.selfReported')
      : mode === 'guided'
        ? t('practice.after.guided', { duration })
        : t('practice.after.silent', { duration });
  const next =
    nextStep &&
    nextStep.practiceId &&
    nextStep.practiceId !== practiceId &&
    (nextStep.reason === 'next_in_path' || nextStep.reason === 'review')
      ? nextStep
      : null;

  return (
    <section className="pp-after" aria-labelledby="pp-after-title">
      <h2 id="pp-after-title" className="pp-after-title" tabIndex={-1}>
        {t('practice.after.title')}
      </h2>
      <p>{acknowledgement}</p>
      {result.shieldUsed && <p className="pp-note">{t('practice.after.shieldUsed')}</p>}
      <p className="pp-note">
        {t('practice.after.practiceDays', {
          count: result.totalPracticeDays,
          n: formatNumber(lang, result.totalPracticeDays),
        })}
      </p>

      <div className="pp-reflection">
        <p className="pp-reflection-prompt">{t('practice.after.reflectionPrompt')}</p>
        <Link to="/journal" className="btn btn-secondary">
          {t('practice.after.writeReflection')}
        </Link>
      </div>

      <div className="pp-next">
        {next && next.practiceId ? (
          <>
            <p>{localizedPlanText(next, t, lang, true).explanation}</p>
            <Link to={`/practice/${encodeURIComponent(next.practiceId)}`} className="btn btn-primary">
              {t('practice.after.continue')}
            </Link>
          </>
        ) : (
          <Link to="/practices" className="btn btn-primary">
            {t('practice.after.chooseAnother')}
          </Link>
        )}
        <Link to="/home" className="link-back">
          {t('practice.backToToday')}
        </Link>
      </div>
    </section>
  );
}
